/**
 * CDN-hosted brand + onboarding image URLs.
 *
 * These SVGs live in the OptiSigns CDN (`scio/assets/…`) rather than the app's `public/` folder so
 * they can be updated without a redeploy. `VITE_CDN_URL` lets non-prod envs point at a different CDN;
 * it falls back to the prod host (same pattern as `playlist-constants` / `useWizardContent`).
 *
 * Logos: two full-logo files (light + dark) because a CDN `<img>` can't be recolored by the app's
 * `.dark` class — the wordmark text color is baked per file. The mark is a single green SVG that
 * reads on both themes.
 */
const CDN_BASE_URL = import.meta.env.VITE_CDN_URL || 'https://cdn.optisigns.com';

export const OPTISIGNS_LOGO = {
  /** Full lockup (cloud mark + wordmark), dark text — for light backgrounds. */
  light: `${CDN_BASE_URL}/scio/assets/logo/optisigns-logo-light.svg`,
  /** Full lockup (cloud mark + wordmark), white text — for dark backgrounds. */
  dark: `${CDN_BASE_URL}/scio/assets/logo/optisigns-logo-dark.svg`,
  /** Collapsed cloud mark only (green, works on light + dark). */
  mark: `${CDN_BASE_URL}/scio/assets/logo/optisigns-mark.svg`,
} as const;

/**
 * Real dashboard screenshots for the pre-hook modal's TV, keyed by `AppMeta.appType`.
 *
 * An app with NO entry keeps the animated placeholder — see
 * `features/app-intent/components/PrehookPreview`.
 *
 * An explicit map rather than a URL derived from the app type (`…/app-preview/${appType}.webp`)
 * so the component knows BEFORE it renders whether a screenshot exists. A derived URL only
 * reveals a miss when the request fails, which costs a round trip on every app we have no
 * picture for; the price of the map is one line per new screenshot.
 *
 * Filenames carry a version suffix on purpose. The CDN serves these with a 24-hour browser
 * cache and no cache-busting in the path, so a replacement is uploaded as `-v2` and swapped
 * here — never written over the existing file, which viewers would keep missing for a day.
 *
 * Shoot at 1088x608 (2x the 544x304 screen in the design) and keep each under ~150 KB.
 */
export const APP_PREVIEW_IMAGES: Readonly<Record<string, string>> = {
  /** Microsoft's Contoso "Sales Overview" sample report — fictional data, no customer's figures. */
  'microsoft-bi': `${CDN_BASE_URL}/scio/assets/app-preview/microsoft-bi-v1.webp`,
};

/**
 * Billing / lifecycle dialog artwork.
 *
 * CDN-hosted for the same reason as the signup artwork above — the design iterates
 * independently of the app, so a re-export should not need a redeploy. WebP because the
 * collage needs BOTH transparency (it floats over the dialog's mint gradient panel) and
 * lossy compression (it contains photographic cloud/monitor illustrations).
 */
export const BILLING_ARTWORK = {
  /** Trial-expired / inactive-account dialog — transparent collage, bottom-anchored. */
  trialExpired: `${CDN_BASE_URL}/scio/assets/billing/trial-expired.webp`,
} as const;
